import { InjectionToken } from '@angular/core';

import { AppType } from './app-type';
import { BuildType } from './build-type';
import { MenuItem } from './menu-item';
import { Role } from './role';

export const ENVIRONMENT = new InjectionToken<Environment>('environment');

export class Environment {
  production: boolean;
  buildType: BuildType;
  app: AppType;
  loginRedirectionRoute: string[];
  defaultRoute: string[];
  versionCheckInterval: number;
  urls: {
    captain?: string;
    donate?: string;
    baseUrl: string;
    taxReceiptUrl?: string;
    receiptUrl?: string;
    zendesk?: string;
    stripeUrl: string;
    resetPasswordUrl: string;
  };
  mapBoxAccessToken: string;
  supportNumber?: string;
  appSignal: string;
  stripeKey: string;
  trackLocation?: boolean;
  dynamicAfterLoginRedirect?: boolean;
  optimizelyKey?: string;
  menu: MenuItem[];
  captchaKey?: string;
  googleClientId: string;

  constructor(payload: IEnvironment) {
    this.production = payload?.production !== false;
    this.buildType = payload.buildType || BuildType.DEV;
    const url = this.isProductionBuild
      ? ''
      : this.isTestBuild
        ? 'testing.'
        : this.isDevBuild
          ? 'dev.'
          : this.isStagingBuild
            ? 'staging.'
            : 'demo.';
    this.app = payload?.app;

    this.loginRedirectionRoute =
      payload?.loginRedirectionRoute ?? (this.isDriver ? ['/', 'donations', 'truck-selection'] : ['/', 'donations']);
    this.defaultRoute = payload?.defaultRoute ?? ['/', 'donations'];

    this.mapBoxAccessToken =
      payload?.mapBoxAccessToken ??
      (this.buildType === BuildType.PRODUCTION
        ? 'pk.eyJ1IjoicmVzdXBwbHlqb2UiLCJhIjoiY2xiMHV1cnNoMTZ5YzNwbTl6NGtkeHZtayJ9.-LJtsSzCoCCGJyVTi0jsnw'
        : 'pk.eyJ1IjoiZW5naW5lZXJpbmctcmVzdXBwbHkiLCJhIjoiY211bWpsam9mMDBrcTJ6czhoazdnamJycSJ9.1NGNIUe8CnQ0MqaChlarWw');
    // Captain SSO (RP-4302): the public Google OAuth client id — one for production, one for every other build
    // (localhost, dev, staging); each must match the API's CAPTAIN_GOOGLE_CLIENT_ID on that environment
    this.googleClientId =
      payload?.googleClientId ||
      (this.isProductionBuild
        ? '536894539664-uustfcvgrkonbguv97n12c2t02n8r4oj.apps.googleusercontent.com'
        : '536894539664-ucnnf8o2fqqv3cs9j5aen9rjhrf1b3f7.apps.googleusercontent.com');
    this.versionCheckInterval = payload?.versionCheckInterval ?? 1000 * 60 * (this.isProductionBuild ? 30 : 10);
    this.supportNumber = payload?.supportNumber ?? (this.isTpl || this.isDriver ? '(781) 355-9453' : undefined);
    // The donor web flow lives on the donate domain now. donor.* redirects there on
    // production only, so anything built for dev/staging has to name donate directly —
    // that redirect gap is what made receipt links land on the retired app (RP-4185).
    const donateBase = this.isProductionBuild ? 'https://donate.resupply.com/' : `https://donate.${url}${this.urlLastPart}/`;

    this.urls = {
      baseUrl: payload?.urls?.baseUrl ?? `https://api.${url}${this.urlLastPart}`,
      /**
       * Base for the tax receipt, which is no longer its own app on `taxreceipt.*` but a
       * page of the donate app: `<base>/<donationCode>/tax-receipt`. That one route replaces
       * the old `/c` (captain, charity) one.
       */
      taxReceiptUrl: payload?.urls?.taxReceiptUrl ?? (!this.isTpl ? `${donateBase}donations` : undefined),
      receiptUrl: (payload?.urls?.receiptUrl ?? !this.isTpl) ? `${donateBase}receipt` : undefined,
      captain: payload?.urls?.captain ?? `https://captain.${url}${this.urlLastPart}/`,
      donate: payload?.urls?.donate ?? donateBase,
      zendesk:
        this.isTpl || this.isDriver
          ? `http://driversupport.${this.urlLastPart}/`
          : this.isCharity
            ? `http://charitysupport.${this.urlLastPart}/`
            : undefined,
      stripeUrl: this.buildType === BuildType.PRODUCTION ? 'https://dashboard.stripe.com' : 'https://dashboard.stripe.com/test',
      resetPasswordUrl: payload?.urls?.resetPasswordUrl ?? `https://reset.${url}${this.urlLastPart}/`,
    };
    this.appSignal =
      payload.appSignal ||
      (this.isDriver
        ? this.isProductionBuild
          ? '37b1133c-ff7f-490f-8e0b-5e2707893b7f'
          : this.isStagingBuild
            ? '9a750cd3-3c78-4d16-a56c-200c21e19583'
            : this.isDevBuild
              ? 'ef0b3a5b-6cb2-4d68-b3a0-227b7586548f'
              : ''
        : '');
    this.stripeKey =
      payload?.stripeKey ||
      (this.isCaptain || this.isZendesk || this.isCharity
        ? this.isProductionBuild
          ? 'pk_live_51IbPzLION1witdQTAnASC69EjzZgS48HlVTFEleX2KSAZEbmng5Mo16WjaMk4LQ0BmXLniqxd90cDzeG5YXuL3vL00pG9KD8Hq'
          : this.isStagingBuild
            ? 'pk_test_51QY5CmIcvjPXTbYf1DxCMc41RmgVhF9cRRvJ3msYLtihnNwtXguUUryZY4zq8MjgIw2YzbqrqfyYLr5z22z4XUzL00OAY8nzwh'
            : 'pk_test_51IbPzLION1witdQTh0yDNNherDREO1jEcRYbfnIq0kUQqwLvuxcLMZqt0Yrme6HrNss8LMGM1C5sltzq0yKgFECT00tJWt123m'
        : '');
    this.trackLocation = payload?.trackLocation !== undefined ? payload.trackLocation : !!this.isDriver;
    this.dynamicAfterLoginRedirect = payload?.dynamicAfterLoginRedirect !== undefined ? payload.dynamicAfterLoginRedirect : !this.isDriver;
    this.optimizelyKey =
      payload?.optimizelyKey ||
      (this.isDevBuild
        ? 'WoDqyLGED1bPvg7Cm2q8d'
        : this.isStagingBuild
          ? '7DnWQ1aRRSghkv3hyTdw5'
          : this.isProductionBuild
            ? '8h4Zq4aWXUxs5ohSrDXg8'
            : this.isDemoBuild
              ? 'CXzdbMYRRPY2vzKKC9EWg'
              : undefined);

    this.menu = this.getMenu();

    this.captchaKey = this.isDevBuild
      ? '6LfBPBgrAAAAAN00CLoRB5ayUXv_5Vc3-GIWpY3i'
      : this.isStagingBuild
        ? '6LfgPhgrAAAAAH98xmwdTTC_bJ4Zrcka_4uYaHkB'
        : this.isDemoBuild
          ? '6LcjhhkrAAAAACuhs8390RtaqsoEEHH-DHSVO-YK'
          : this.isProductionBuild
            ? '6LcmjxkrAAAAANAvTwnnPr7jDLN_B3f0-Wrpeblk'
            : undefined;
  }

  getMenu() {
    const captainMenu: MenuItem[] = [
      {
        svgIcon: 'parachute-icon',
        title: () => 'Donations',
        route: ['/', 'donations'],
      },
      {
        icon: 'map',
        title: () => 'Journeys',
        route: ['/', 'journeys'],
        visible: () => !this.isZendesk,
      },
      {
        svgIcon: 'charities-icon',
        title: () => 'Charities',
        route: ['/', 'charities'],
        visible: () => !this.isZendesk,
      },
      {
        svgIcon: 'truck-icon',
        title: () => 'Partners',
        route: ['/', 'partners'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'business',
        title: () => 'Extra Miles',
        route: ['/', 'xmiles'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'corporate_fare',
        title: () => 'Organizations',
        route: ['/', 'organizations'],
        visible: () => !this.isZendesk,
      },
      {
        svgIcon: 'users-icon',
        title: () => 'Users',
        route: ['/', 'users'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'hive',
        title: () => 'Markets',
        route: ['/', 'markets'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'zoom_in',
        title: () => 'TRP Map',
        route: ['/', 'trp'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'support_agent',
        title: () => 'Phone Leads',
        route: ['/', 'phone-leads'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'archive',
        title: () => 'Manual Leads',
        route: ['/', 'manual-leads'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'notifications',
        title: () => 'Notifications',
        route: ['/', 'notifications'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'phone_forwarded',
        title: () => 'Phone Configurations',
        route: ['/', 'phone-configurations'],
        visible: (data) => {
          return !this.isZendesk && data.permissions?.phoneConfigurations;
        },
      },
      {
        icon: 'route',
        title: () => 'Playback',
        route: ['/', 'playback'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'star',
        title: () => 'Public Reviews',
        route: ['/', 'public-reviews'],
        visible: (data) => !this.isZendesk && data.permissions?.appConfiguration,
      },
      {
        icon: 'chair',
        title: () => 'Custom Items',
        route: ['/', 'custom-items'],
        visible: (data) => !this.isZendesk && data.permissions?.itemApproval,
      },
      {
        icon: 'toggle_on',
        title: () => 'Feature Flags',
        route: ['/', 'features'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'report',
        title: () => 'Concierge',
        route: ['/', 'concierge'],
        visible: () => !this.isZendesk,
      },
      {
        icon: 'campaign',
        title: () => 'Campaigns',
        route: ['/', 'campaigns'],
        visible: () => {
          return !this.isZendesk;
        },
      },
      {
        icon: 'rate_review',
        title: () => 'Surveys',
        route: ['/', 'surveys'],
        visible: (data) => {
          return !this.isZendesk && data.permissions?.surveys;
        },
      },
    ];

    const charityMenu: MenuItem[] = [
      {
        svgIcon: 'parachute-icon',
        title: () => 'Donations',
        route: ['/', 'slider'],
        visible: () => this.isDevBuild,
      },
      {
        svgIcon: 'parachute-icon',
        title: () => (this.isDevBuild ? 'Schedule' : 'Donations'),
        route: ['/', 'donations'],
      },
      {
        icon: 'table_rows',
        title: () => 'Data',
        route: ['/', 'data'],
      },
      {
        icon: 'people',
        title: () => 'Donors',
        route: ['/', 'donors'],
      },
      {
        svgIcon: 'charities-icon',
        title: () => 'Stores',
        route: ['/', 'stores'],
      },
      {
        svgIcon: 'users-icon',
        title: () => 'Users',
        route: ['/', 'users'],
        visible: (data) => !data.roles.includes(Role.STORE),
      },
      {
        icon: 'settings',
        title: () => 'Settings',
        route: ['/', 'settings'],
        visible: (data) => !data.roles.includes(Role.STORE),
      },
      {
        icon: 'settings',
        title: () => 'Settings',
        route: ['/', 'stores', 'settings'],
        visible: (data) => data.roles.includes(Role.STORE),
      },
      {
        svgIcon: 'sponsorship',
        title: () => 'Sponsorship',
        visible: (data) => data.charity?.discount,
        route: ['/'],
        expanded: ['sponsorship/settings', 'sponsorship/account-statements', 'sponsorship/invoices'],
        children: [
          // {
          //   icon: 'dashboard',
          //   title: () => 'Dashboard',
          //   route: ['/', ''],
          // },
          {
            icon: 'settings',
            visible: (data) => data.permissions.sponsorships,
            title: () => 'Sponsorship Settings',
            route: ['/', 'sponsorship', 'settings'],
          },
          {
            icon: 'swap_horiz',
            title: () => 'Account Statements',
            route: ['/', 'sponsorship', 'account-statements'],
          },
          {
            icon: 'receipt_long',
            title: () => 'Invoices',
            route: ['/', 'sponsorship', 'invoices'],
          },
        ],
      },
      {
        icon: 'bed',
        title: () => 'Items',
        route: ['/', 'items'],
        visible: (data) => data.roles.includes(Role.CHARITY) && data.charity?.items,
      },
      {
        icon: 'help_outline',
        title: () => 'Help Center',
        externalLink: this.urls.zendesk,
        visible: () => !!this.urls.zendesk,
      },
    ];

    const partnerMenu: MenuItem[] = [
      {
        svgIcon: 'parachute-icon',
        title: () => 'Donations',
        route: ['/', 'donations'],
      },
      {
        icon: 'av_timer',
        title: () => 'Pending Donations',
        route: ['/', 'slider'],
        counter: 'pendingCount',
      },
      {
        icon: 'map',
        title: () => 'Journeys',
        route: ['/', 'journeys'],
        visible: (data) => data.partner?.journey,
      },
      {
        svgIcon: 'truck-icon',
        title: () => 'Trucks',
        route: ['/', 'trucks'],
        visible: () => !this.isZendesk,
      },
      {
        svgIcon: 'users-icon',
        title: () => 'Users',
        route: ['/', 'users'],
      },
      {
        svgIcon: 'square-dolar',
        title: () => 'Payments',
        route: ['/', 'payments'],
        subtitle: {
          show: (data) => data.partner && !data.partner?.payment?.setupCompleted,
          value: ' (Stripe account setup required)',
          isWarning: true,
        },
      },
      {
        icon: 'star',
        title: () => 'Reviews',
        route: ['/', 'reviews'],
      },
      {
        icon: 'route',
        title: () => 'Route Planning',
        route: ['/', 'planning'],
        visible: (data) => !data.partner?.journey,
      },
      {
        icon: 'settings',
        title: () => 'Settings',
        route: ['/', 'settings'],
      },
      {
        icon: 'help_outline',
        title: () => 'Help Center',
        externalLink: this.urls.zendesk,
        visible: () => !!this.urls.zendesk,
      },
    ];

    return this.isCaptain || this.isZendesk ? captainMenu : this.isCharity ? charityMenu : this.isTpl ? partnerMenu : [];
  }

  get urlLastPart() {
    return this.isDemoBuild ? 'resupply.cloud' : 'resupplyapp.com';
  }

  get isDevBuild(): boolean {
    return this.buildType === BuildType.DEV || this.buildType === BuildType.TEST;
  }

  get isTestBuild(): boolean {
    return this.buildType === BuildType.TEST;
  }

  get isStagingBuild(): boolean {
    return this.buildType === BuildType.STAGING;
  }

  get isDemoBuild(): boolean {
    return this.buildType === BuildType.DEMO;
  }

  get isProductionBuild(): boolean {
    return this.buildType === BuildType.PRODUCTION;
  }

  get isCaptain(): boolean {
    return this.app === AppType.CAPTAIN;
  }

  get isCharity(): boolean {
    return this.app === AppType.CHARITY;
  }

  get isTpl(): boolean {
    return this.app === AppType.TPL;
  }

  get isDriver(): boolean {
    return this.app === AppType.DRIVER;
  }

  get isZendesk(): boolean {
    return this.app === AppType.ZENDESK;
  }
}

export interface IEnvironment {
  production?: boolean;
  buildType?: BuildType;
  app: AppType;
  loginRedirectionRoute?: string[];
  defaultRoute?: string[];
  versionCheckInterval?: number;
  urls?: {
    captain?: string;
    donate?: string;
    baseUrl?: string;
    webFlowBaseUrl?: string;
    taxReceiptUrl?: string;
    receiptUrl?: string;
    resetPasswordUrl?: string;
  };
  mapBoxAccessToken?: string;
  supportNumber?: string;
  appSignal?: string;
  stripeKey?: string;
  trackLocation?: boolean;
  dynamicAfterLoginRedirect?: boolean;
  optimizelyKey?: string;
  googleClientId?: string;
}
